import { createContext, useCallback, useContext, useState } from "react";
import { CheckCircle2, X, XCircle, Info } from "lucide-react";
import { cn } from "@/packages/utils/cn";

type Tone = "success" | "error" | "info";

interface Toast {
  id: string;
  title: string;
  description?: string;
  tone: Tone;
}

interface ToastApi {
  toast: (t: Omit<Toast, "id"> & { durationMs?: number }) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const TONES: Record<Tone, { wrap: string; bar: string; Icon: typeof Info }> = {
  success: {
    wrap: "border-signal-ok/40 bg-card-paper",
    bar: "bg-signal-ok",
    Icon: CheckCircle2,
  },
  error: {
    wrap: "border-signal-red/40 bg-card-paper",
    bar: "bg-signal-red",
    Icon: XCircle,
  },
  info: {
    wrap: "border-rule bg-card-paper",
    bar: "bg-signal-lavender",
    Icon: Info,
  },
};

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback<ToastApi["toast"]>((t) => {
    const id = crypto.randomUUID();
    const duration = t.durationMs ?? 5000;
    setToasts((prev) => [
      ...prev,
      { id, title: t.title, description: t.description, tone: t.tone },
    ]);
    if (duration > 0) {
      setTimeout(() => dismiss(id), duration);
    }
  }, [dismiss]);

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <div className="fixed bottom-4 right-4 z-[60] flex flex-col gap-2 w-full max-w-sm pointer-events-none">
        {toasts.map((t) => {
          const tone = TONES[t.tone];
          const Icon = tone.Icon;
          return (
            <div
              key={t.id}
              role="status"
              className={cn(
                "pointer-events-auto relative overflow-hidden border rounded-lg shadow-lg pl-4 pr-3 py-3 flex items-start gap-3 animate-in fade-in slide-in-from-bottom-2",
                tone.wrap,
              )}
            >
              <div
                className={cn("absolute left-0 top-0 bottom-0 w-1", tone.bar)}
                aria-hidden="true"
              />
              <Icon
                className={cn(
                  "size-4 mt-0.5 shrink-0",
                  t.tone === "success" && "text-signal-ok",
                  t.tone === "error" && "text-signal-red",
                  t.tone === "info" && "text-signal-lavender",
                )}
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold leading-tight">
                  {t.title}
                </div>
                {t.description && (
                  <div className="text-xs text-muted-foreground mt-0.5 break-words">
                    {t.description}
                  </div>
                )}
              </div>
              <button
                onClick={() => dismiss(t.id)}
                className="text-faint hover:text-foreground"
                aria-label="Dismiss"
              >
                <X className="size-3.5" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within ToastProvider");
  return ctx;
}

